// Recovery from stale app versions after a publish.
// 1) Unregisters any leftover app-shell service worker (/sw.js).
// 2) Reloads the page once when a lazily-loaded file is missing (old HTML
//    pointing to deleted chunks). A sessionStorage guard prevents loops.

const RELOAD_KEY = "piktable:chunk-reload-at";
const RELOAD_WINDOW_MS = 60_000;

export function isChunkLoadError(message: string): boolean {
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed|Unable to preload CSS/i.test(
    message,
  );
}

export function shouldReloadOnce(storage: Pick<Storage, "getItem" | "setItem">, now: number): boolean {
  try {
    const last = Number(storage.getItem(RELOAD_KEY) || 0);
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    storage.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function reloadOnce() {
  if (shouldReloadOnce(window.sessionStorage, Date.now())) {
    window.location.reload();
  }
}

export function installStaleAppRecovery() {
  if (typeof window === "undefined") return;

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) =>
        regs.forEach((reg) => {
          const url = (reg.active || reg.waiting || reg.installing)?.scriptURL || "";
          if (/\/(sw|service-worker)\.js$/.test(url)) reg.unregister().catch(() => {});
        }),
      )
      .catch(() => {});
  }

  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as { message?: string } | string | undefined;
    const message = typeof reason === "string" ? reason : reason?.message || "";
    if (isChunkLoadError(message)) reloadOnce();
  });
}
